/**
 * Hook for fetching documents by client
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { Document } from '../types/management';
import {
  fetchDocuments,
  unassignDocument as unassignDocumentApi,
} from '../lib/management-api';
import { useAuth } from '../components/Auth';
import CONFIG from '../config';

interface UseDocumentsResult {
  documents: Document[];
  isLoading: boolean;
  error: string | null;
  hasFetched: boolean;
  refetch: () => void;
  unassign: (s3Key: string) => Promise<{ alreadyUnassigned: boolean }>;
}

/**
 * Hook to fetch documents for a specific client
 *
 * @param clientFilter - Client slug to filter by, or null to skip fetching
 * @returns Object with documents data, loading state, error, and refetch function
 */
export function useDocuments(clientFilter: string | null): UseDocumentsResult {
  const { getAuthHeader, isAuthenticated } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refetch = useCallback(() => {
    if (mountedRef.current) {
      setFetchTrigger((prev) => prev + 1);
    }
  }, []);

  useEffect(() => {
    // Don't fetch if not authenticated, no client filter, or 'pending' filter
    // (pending is handled by usePendingClassifications hook)
    if (!isAuthenticated || !clientFilter || clientFilter === 'pending') {
      setDocuments([]);
      setIsLoading(false);
      setHasFetched(false);
      return;
    }

    const abortController = new AbortController();
    // Store validated filter for use in async function (TypeScript narrowing)
    const filter = clientFilter;

    async function loadDocuments() {
      setIsLoading(true);
      setError(null);

      try {
        const authHeader = getAuthHeader();
        if (!authHeader && !CONFIG.DEV_MODE) {
          throw new Error('No authentication token available');
        }

        const headers: Record<string, string> = authHeader
          ? { Authorization: authHeader }
          : {};
        const data = await fetchDocuments(
          filter,
          headers,
          abortController.signal,
        );
        setDocuments(data.documents);
        setHasFetched(true);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return; // Ignore abort errors
        }
        setError(
          err instanceof Error ? err.message : 'Failed to fetch documents',
        );
      } finally {
        setIsLoading(false);
      }
    }

    loadDocuments();

    return () => {
      abortController.abort();
    };
  }, [isAuthenticated, getAuthHeader, clientFilter, fetchTrigger]);

  const unassign = useCallback(
    async (s3Key: string): Promise<{ alreadyUnassigned: boolean }> => {
      const authHeader = getAuthHeader();
      if (!authHeader && !CONFIG.DEV_MODE) {
        throw new Error('Not authenticated');
      }

      const headers: Record<string, string> = authHeader
        ? { Authorization: authHeader }
        : {};
      const result = await unassignDocumentApi(s3Key, headers);
      refetch();
      return { alreadyUnassigned: result.status === 'already_unassigned' };
    },
    [getAuthHeader, refetch],
  );

  return {
    documents,
    isLoading,
    error,
    hasFetched,
    refetch,
    unassign,
  };
}
